// ══════════════════════════════════════════════════════════════════════════════
// PAGE AGENDA PRO - Calendrier Google + Événements avec Google Meet
// AbracadaWorld - Cockpit Pro (Artisans/Vendeurs)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, Video, MapPin,
  Clock, Users, X, Check, ExternalLink, Link2, RefreshCw, LogOut,
  ArrowLeft, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
};

const eventColors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];

export default function AgendaProPage() {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [newEvent, setNewEvent] = useState({
    summary: '',
    description: '',
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    attendees: '',
    add_meet: true
  });

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!showCreateModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showCreateModal, isMobile]);

  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) loadEvents();
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const connectGoogle = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter Google ?')) return;
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setEvents([]);
      toast.success('Déconnecté');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      
      const res = await fetch(
        `${API_URL}/api/google/calendar/events?time_min=${startOfMonth.toISOString()}&time_max=${endOfMonth.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    if (!newEvent.summary || !newEvent.date) {
      toast.error('Titre et date obligatoires');
      return;
    }

    try {
      const token = getToken();
      const startDateTime = `${newEvent.date}T${newEvent.start_time}:00`;
      const endDateTime = `${newEvent.date}T${newEvent.end_time}:00`;
      const attendees = newEvent.attendees.split(',').map(e => e.trim()).filter(e => e.includes('@'));

      const res = await fetch(`${API_URL}/api/google/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          summary: newEvent.summary,
          description: newEvent.description,
          start_datetime: startDateTime,
          end_datetime: endDateTime,
          attendees,
          add_meet: newEvent.add_meet
        })
      });

      if (res.ok) {
        toast.success(newEvent.add_meet ? 'RDV créé avec Meet !' : 'RDV créé !');
        setShowCreateModal(false);
        setNewEvent({ summary: '', description: '', date: '', start_time: '09:00', end_time: '10:00', attendees: '', add_meet: true });
        loadEvents();
      } else {
        toast.error('Erreur de création');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer ce RDV ?')) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Supprimé');
        setSelectedEvent(null);
        loadEvents();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  useEffect(() => {
    checkGoogleStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Google connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (googleStatus.connected) loadEvents();
  }, [currentDate, googleStatus.connected]);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() || 7;
    
    const days = [];
    for (let i = startingDay - 1; i > 0; i--) {
      const prevDate = new Date(year, month, 1 - i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  const getEventsForDay = (date) => events.filter(event => {
    const eventDate = new Date(event.start);
    return eventDate.toDateString() === date.toDateString();
  });

  const formatDate = (dateString) => new Date(dateString).toLocaleString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
  });

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-8 text-center">
          {/* Badge Beta */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            Version Beta
          </div>
          
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center">
            <Calendar size={36} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Agenda Pro</h1>
          <p className="text-gray-500 mb-6">Synchronise ton calendrier pour gérer tes rendez-vous clients et créer des visios.</p>
          
          {/* Google Calendar - Disponible */}
          <button
            onClick={connectGoogle}
            data-testid="connect-calendar-pro-btn"
            className="w-full py-4 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors mb-3"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-6 h-6" />
            <span className="font-semibold text-gray-700">Google Calendar</span>
            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
          </button>
          
          {/* Outlook Calendar - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 mb-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              <path d="M12 2v20M2 7l10 5 10-5" stroke="#fff" strokeWidth="1"/>
            </svg>
            <span className="font-semibold text-gray-500">Outlook Calendar</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          {/* iCloud Calendar - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M19.5 10.5c0-4.14-3.36-7.5-7.5-7.5S4.5 6.36 4.5 10.5c0 .17.01.34.02.5A5.5 5.5 0 006 21.5h12a5.5 5.5 0 001.5-10.8c0-.07 0-.13 0-.2z" fill="#007AFF"/>
            </svg>
            <span className="font-semibold text-gray-500">iCloud Calendar</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          <p className="mt-6 text-xs text-gray-400">
            Tes données restent privées et sécurisées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Pro */}
      <div 
        className="sticky top-0 z-20 px-4 py-4"
        style={{ background: 'linear-gradient(135deg, #1a2a4a 0%, #2d4a6a 100%)', paddingTop: 'max(16px, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-white">Mon Agenda</h1>
            {googleStatus.email && <p className="text-white/60 text-sm">{googleStatus.email}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreateModal(true)} className="w-10 h-10 rounded-full bg-[#E8650A] flex items-center justify-center shadow-lg">
              <Plus size={22} className="text-white" />
            </button>
            <button onClick={disconnectGoogle} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <LogOut size={18} className="text-white/70" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded-full">
            <ChevronLeft size={24} className="text-white" />
          </button>
          <h2 className="text-lg font-semibold text-white">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
          <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded-full">
            <ChevronRight size={24} className="text-white" />
          </button>
        </div>
      </div>

      {/* Calendrier */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {getDaysInMonth().map((day, index) => {
            const dayEvents = getEventsForDay(day.date);
            const isToday = day.date.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-[80px] p-1 rounded-lg border ${
                  day.isCurrentMonth ? 'bg-white border-gray-100' : 'bg-gray-50 border-transparent'
                } ${isToday ? 'ring-2 ring-[#E8650A]' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${day.isCurrentMonth ? 'text-gray-800' : 'text-gray-400'} ${isToday ? 'text-[#E8650A]' : ''}`}>
                  {day.date.getDate()}
                </div>
                
                {dayEvents.slice(0, 2).map((event, i) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer mb-0.5 ${eventColors[i % eventColors.length]} text-white`}
                  >
                    {event.summary}
                  </div>
                ))}
                
                {dayEvents.length > 2 && <div className="text-[10px] text-gray-500 px-1">+{dayEvents.length - 2}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste événements */}
      <div className="p-4 pb-32">
        <h3 className="font-semibold text-gray-800 mb-3">Prochains RDV</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-[#1a2a4a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Aucun événement</div>
        ) : (
          <div className="space-y-3">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSelectedEvent(event)}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-1 h-12 rounded-full ${eventColors[index % eventColors.length]}`} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">{event.summary}</h4>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <Clock size={14} />
                      {formatDate(event.start)}
                    </p>
                    {event.meet_link && (
                      <a
                        href={event.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 mt-2"
                      >
                        <Video size={14} />
                        Rejoindre Meet
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {createPortal(
        <AnimatePresence>
          {selectedEvent && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-[10002]" onClick={() => setSelectedEvent(null)} />
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[10002] max-h-[80vh] overflow-y-auto"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">{selectedEvent.summary}</h2>
                  <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <Clock size={20} />
                    <span>{formatDate(selectedEvent.start)}</span>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin size={20} />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}

                  {selectedEvent.attendees?.length > 0 && (
                    <div className="flex items-start gap-3 text-gray-600">
                      <Users size={20} className="mt-0.5" />
                      <div>{selectedEvent.attendees.map((email, i) => <div key={i}>{email}</div>)}</div>
                    </div>
                  )}

                  {selectedEvent.meet_link && (
                    <a
                      href={selectedEvent.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 text-white rounded-2xl font-semibold hover:bg-green-600"
                    >
                      <Video size={20} />
                      Rejoindre Google Meet
                    </a>
                  )}

                  <button
                    onClick={() => deleteEvent(selectedEvent.id)}
                    className="w-full py-3 border-2 border-red-200 text-red-600 rounded-2xl font-medium hover:bg-red-50"
                  >
                    Supprimer le RDV
                  </button>
                </div>
              </div>
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Modal création - Version mobile plein écran / Desktop centré */}
      {createPortal(
        <AnimatePresence>
          {showCreateModal && (
            isMobile ? (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-[10002] flex flex-col bg-white"
            >
              {/* Header */}
              <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
                <button onClick={() => setShowCreateModal(false)} className="p-1 text-gray-500">
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Nouveau RDV</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
                    <input
                      type="text"
                      value={newEvent.summary}
                      onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                      placeholder="RDV Client..."
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Date *</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Début</label>
                      <input
                        type="time"
                        value={newEvent.start_time}
                        onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                        className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Fin</label>
                      <input
                        type="time"
                        value={newEvent.end_time}
                        onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                        className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Participants</label>
                    <input
                      type="text"
                      value={newEvent.attendees}
                      onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                      placeholder="client@email.com"
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  <div 
                    className={`p-4 rounded-2xl border-2 cursor-pointer ${newEvent.add_meet ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    onClick={() => setNewEvent({ ...newEvent, add_meet: !newEvent.add_meet })}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${newEvent.add_meet ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                        {newEvent.add_meet && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Video size={18} className={newEvent.add_meet ? 'text-green-600' : 'text-gray-400'} />
                          <span className="font-medium text-gray-800">Ajouter Google Meet</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">Lien de visio créé automatiquement</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createEvent}
                    disabled={!newEvent.summary || !newEvent.date}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Calendar size={18} />
                    Créer
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-[10002]" onClick={() => setShowCreateModal(false)} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-[10002] flex items-center justify-center p-4"
              >
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-800">Nouveau RDV</h2>
                    <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                      <input
                        type="text"
                        value={newEvent.summary}
                        onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                        placeholder="RDV Client..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a2a4a] focus:ring-1 focus:ring-[#1a2a4a]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a2a4a] focus:ring-1 focus:ring-[#1a2a4a]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Début</label>
                        <input
                          type="time"
                          value={newEvent.start_time}
                          onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fin</label>
                        <input
                          type="time"
                          value={newEvent.end_time}
                          onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Participants (emails)</label>
                      <input
                        type="text"
                        value={newEvent.attendees}
                        onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                        placeholder="client@email.com"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200"
                      />
                    </div>

                    <div 
                      className={`p-4 rounded-2xl border-2 cursor-pointer ${newEvent.add_meet ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                      onClick={() => setNewEvent({ ...newEvent, add_meet: !newEvent.add_meet })}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${newEvent.add_meet ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                          {newEvent.add_meet && <Check size={14} className="text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Video size={18} className={newEvent.add_meet ? 'text-green-600' : 'text-gray-400'} />
                            <span className="font-medium text-gray-800">Ajouter Google Meet</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">Lien de visio créé automatiquement</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                      <button
                        className="px-4 py-3 rounded-xl font-semibold text-white flex items-center gap-2"
                        style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                      >
                        <Sparkles size={16} />
                        Demander à Abra
                      </button>
                      <button
                        onClick={createEvent}
                        className="flex-1 py-3 bg-[#E8650A] text-white rounded-xl font-semibold hover:bg-orange-600 flex items-center justify-center gap-2"
                      >
                        <Calendar size={20} />
                        Créer le RDV
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
